import type { ReactNode } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import SharedPage from './pages/SharedPage';
import BrowsePage from './pages/BrowsePage';
import NewDashboardPage from './pages/NewDashboardPage';
import DashboardViewPage from './pages/DashboardViewPage';
import AssetArchivePage from './pages/AssetArchivePage';
import AdminPage from './pages/AdminPage';
import AccessDeniedPage from './pages/AccessDeniedPage';

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

// External accounts have no canvas access (their only feature, Prototype Forge,
// was removed); bounce them to the access-denied page.
function InternalOnly() {
  const { user } = useAuth();
  if (user?.role === 'external') return <Navigate to="/access-denied" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route element={<InternalOnly />}>
            <Route index element={<HomePage />} />
            <Route path="shared" element={<SharedPage />} />
            <Route path="browse" element={<BrowsePage />} />
            <Route path="new" element={<NewDashboardPage />} />
            <Route path="dashboard/:id" element={<DashboardViewPage />} />
            <Route path="asset-archive" element={<AssetArchivePage />} />
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
